import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { vonDataService } from '@/services/vonDataService';
import { VonSummary, VON_SECTION_NAMES } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Edit2, Eye, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface PrematuritateSummaryViewProps {
  patientId: number;
  onEditClick?: (section: string) => void;
  readOnly?: boolean;
}

export const PrematuritateSummaryView: React.FC<PrematuritateSummaryViewProps> = ({
  patientId,
  onEditClick,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<VonSummary | null>(null);

  useEffect(() => {
    loadSummary();
  }, [patientId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getSummary(patientId);
      setSummary(data || null);
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-au putut încărca datele rezumative',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-sm text-muted-foreground">Se încarcă datele...</span>
      </div>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-8">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            Nu sunt date RONN disponibile pentru acest pacient
          </p>
          <p className="text-sm text-muted-foreground text-center mt-2">
            Utilizați tab-urile de mai sus pentru a introduce date
          </p>
        </CardContent>
      </Card>
    );
  }

  const { completeness } = summary;

  // Section status indicators
  const sections = [
    { key: 'perinatal', data: summary.perinatal, label: VON_SECTION_NAMES.perinatal },
    { key: 'maternal', data: summary.maternal, label: VON_SECTION_NAMES.maternal },
    { key: 'resuscitation', data: summary.resuscitation, label: VON_SECTION_NAMES.resuscitation },
    { key: 'resp_support', data: summary.resp_support, label: VON_SECTION_NAMES.resp_support },
    { key: 'resp_course', data: summary.resp_course, label: VON_SECTION_NAMES.resp_course },
    { key: 'cardiovascular', data: summary.cardiovascular, label: VON_SECTION_NAMES.cardiovascular },
    { key: 'gi_nutrition', data: summary.gi_nutrition, label: VON_SECTION_NAMES.gi_nutrition },
    { key: 'renal', data: summary.renal, label: VON_SECTION_NAMES.renal },
    { key: 'neurology', data: summary.neurology, label: VON_SECTION_NAMES.neurology },
    { key: 'ophthalmology', data: summary.ophthalmology, label: VON_SECTION_NAMES.ophthalmology },
    { key: 'infections', data: summary.infections, label: VON_SECTION_NAMES.infections },
    { key: 'congenital', data: summary.congenital, label: VON_SECTION_NAMES.congenital },
    { key: 'genetic', data: summary.genetic, label: VON_SECTION_NAMES.genetic },
    { key: 'outcomes', data: summary.outcomes, label: VON_SECTION_NAMES.outcomes },
    { key: 'readmission', data: summary.readmission, label: VON_SECTION_NAMES.readmission },
  ];

  return (
    <div className="space-y-6">
      {/* Completeness Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Status completare date RONN</span>
            <Badge variant={completeness.percentage >= 80 ? 'default' : 'secondary'}>
              {completeness.completed_sections} / {completeness.total_sections} secțiuni
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Progres general</span>
                <span className="font-semibold">{completeness.percentage.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    completeness.percentage >= 80
                      ? 'bg-green-600'
                      : completeness.percentage >= 50
                      ? 'bg-blue-600'
                      : 'bg-yellow-600'
                  }`}
                  style={{ width: `${completeness.percentage}%` }}
                ></div>
              </div>
            </div>

            {/* Missing Sections Warning */}
            {completeness.missing_sections.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Secțiuni incomplete ({completeness.missing_sections.length})
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Click pe secțiuni pentru a completa datele lipsă
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section Status Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Secțiuni RONN</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sections.map((section) => {
              const hasData = section.data !== null && section.data !== undefined;
              const isMissing = completeness.missing_sections.includes(section.key);

              return (
                <div
                  key={section.key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    hasData
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {hasData ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${hasData ? 'text-green-900' : 'text-gray-600'}`}>
                      {section.label}
                    </span>
                  </div>
                  {/* In read-only mode, only offer to view sections that already have
                      data - never a way into an empty section to start entering data. */}
                  {onEditClick && (!readOnly || hasData) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onEditClick(section.key)}
                      title={readOnly ? 'Vizualizează' : (hasData ? 'Editează' : 'Completează')}
                    >
                      {readOnly ? <Eye className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats (if data available) */}
      {summary.perinatal && (
        <Card>
          <CardHeader>
            <CardTitle>Date generale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summary.perinatal.sex && (
                <div>
                  <p className="text-sm text-muted-foreground">Sex</p>
                  <p className="text-lg font-semibold">
                    {summary.perinatal.sex === 1 ? 'M' : summary.perinatal.sex === 2 ? 'F' : 'N/A'}
                  </p>
                </div>
              )}
              {summary.perinatal.ga_weeks && (
                <div>
                  <p className="text-sm text-muted-foreground">Vârstă gestațională</p>
                  <p className="text-lg font-semibold">
                    {summary.perinatal.ga_weeks} săpt. {summary.perinatal.ga_days || 0} zile
                  </p>
                </div>
              )}
              {summary.perinatal.birthweight_g && (
                <div>
                  <p className="text-sm text-muted-foreground">Greutate la naștere</p>
                  <p className="text-lg font-semibold">{summary.perinatal.birthweight_g} g</p>
                </div>
              )}
              {summary.perinatal.head_circumference && (
                <div>
                  <p className="text-sm text-muted-foreground">Perimetru cranian</p>
                  <p className="text-lg font-semibold">{summary.perinatal.head_circumference} cm</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
